import { useCallback, useState } from "react";
import { paymentStripeApi } from "../../api";
import {
  CreateStripePaymentIntent,
  GuestAddress,
  GuestCartItem,
  GuestCreateStripePaymentIntent,
  ShippingRate,
} from "../../api-client/models";

export interface CreateStripePaymentIntentPayload {
  shopping_car: number;
  address_id: number;
  shipping_cost: number;
  discount_amount: number;
  rates: ShippingRate[];
  currency?: string;
}

export interface CreateGuestStripePaymentIntentPayload {
  guest_email: string;
  guest_first_name?: string;
  guest_last_name?: string;
  items: GuestCartItem[];
  address: GuestAddress;
  shipping_cost: number;
  discount_amount: number;
  rates: ShippingRate[];
  currency?: string;
}

export interface StripePaymentIntentResponse {
  payment_intent_id?: string;
  client_secret?: string;
  clientSecret?: string;
  amount_cents?: number;
  amount?: number;
  currency?: string;
  subtotal?: string;
  tax_rate?: number;
  tax_amount?: string;
  shipping_cost?: string;
  discount?: string;
  total?: string;
  checkout_url?: string;
  checkoutUrl?: string;
}

export const useStripePaymentIntent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createStripePaymentIntent = useCallback(
    async (payload: CreateStripePaymentIntentPayload): Promise<StripePaymentIntentResponse> => {
      setLoading(true);
      setError(null);

      try {
        const requestPayload: CreateStripePaymentIntent = {
          shopping_car: payload.shopping_car,
          address_id: payload.address_id,
          rates: payload.rates,
          shipping_cost: payload.shipping_cost,
          discount_amount: payload.discount_amount,
          currency: payload.currency,
        };

        // Generated typing says void, but backend returns payment intent payload.
        const response = (await paymentStripeApi.paymentsStripeCreatePaymentIntentCreate({
          createStripePaymentIntent: requestPayload,
        })) as unknown as { data: StripePaymentIntentResponse };

        return response.data ?? {};
      } catch (err: any) {
        const message = String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "No se pudo crear el PaymentIntent de Stripe.");
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createGuestStripePaymentIntent = useCallback(
    async (payload: CreateGuestStripePaymentIntentPayload): Promise<StripePaymentIntentResponse> => {
      setLoading(true);
      setError(null);

      try {
        const requestPayload: GuestCreateStripePaymentIntent = {
          guest_email: payload.guest_email,
          guest_first_name: payload.guest_first_name,
          guest_last_name: payload.guest_last_name,
          items: payload.items,
          address: payload.address,
          rates: payload.rates,
          shipping_cost: payload.shipping_cost,
          discount_amount: payload.discount_amount,
          currency: payload.currency,
        };

        const response = (await paymentStripeApi.paymentsStripeGuestPaymentIntentCreate({
          guestCreateStripePaymentIntent: requestPayload,
        })) as unknown as { data: StripePaymentIntentResponse };

        return response.data ?? {};
      } catch (err: any) {
        const message = String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || "No se pudo crear el PaymentIntent guest de Stripe.");
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createStripePaymentIntent,
    createGuestStripePaymentIntent,
    loading,
    error,
  };
};
